import { useState } from 'react';
import {
  estimatePictures, estimatePrompt, fromStandard, getEstimateThinking, wantsEstimate,
} from './dimensionsRun';
import { ImageLightbox } from './ImageLightbox';
import { BoxOverlay } from './BoxOverlay';
import { getParts, type PartItem } from './partsRun';
import { runEstimates, useEstimateRun } from './estimateRun';
import { measureImages } from './imageSize';
import { scaleEstimates, scaleInputs } from './sizeFromScale';
import { listingImageSrc } from './listingCropsRun';
import { directionLabel } from './viewDirections';
import { CallBadge } from './CallBadge';
import { Ruler } from 'lucide-react';
import { useToolSizes } from './toolSizes';
import { reasoningTooltip } from './ReasoningBadge';
import { useLanguage } from '../../contexts/LanguageContext';

// What the estimate call sends, and what it thought — one badge beside the button, opening
// on a menu of the two (see CallBadge).
//
// They matter MORE here than there. The other two size calls transcribe: their answer is
// on the page they read, and checking it is a matter of looking. This one reasons from a
// photograph against a ruler, so the interesting part is not the number but the sentence
// that produced it — "about two thirds of the seat's width" is a claim you can disagree
// with, where "~430 mm" is a number you can only accept.
//
// BESIDE THE PHASE BUTTON, with the others, and its Run is the press: every part with nothing
// measured on "All parts", the open one on its own tab. The panels are about ONE part — under
// "All parts" the first the press would ask about, which is the question in the shape every
// one of them gets.
export function EstimateInfo({ docId, file, items, only, productName, categories, disabled, onStop }: {
  docId: string;
  // The manual, for the tools: their size is measured on its pages (see useToolSizes).
  file: Blob;
  items: PartItem[];
  // The open part, or undefined for every part.
  only?: number;
  productName: string;
  // Every part's category, so the preview asks for the same rows the call will. A prompt
  // that has drifted from the call is worse than none.
  categories: string[];
  // Something else is in flight — the whole press — or the phase is locked. See CallRun.
  disabled: boolean;
  // STOP, while this badge's run is out and was started on its own (see CallRun.onStop).
  onStop?: () => void;
}) {
  const { t } = useLanguage();
  const run = useEstimateRun(docId);
  const estimable = (only == null ? items.map((_, i) => i) : items[only] ? [only] : [])
    .filter((i) => wantsEstimate(items[i], categories[i] ?? ''));
  // THE TOOLS, MEASURED ON THE DRAWINGS FIRST. Arithmetic over the manual search's boxes (see
  // useToolSizes); a tool the drawings size is not put to the model. One they cannot size —
  // not drawn against anything measured — is still estimated from its standard, and the panel
  // says which is which.
  const toolSizes = useToolSizes({ docId, file, items });
  const tools = estimable.filter((i) => items[i]?.tool);
  const drawn = tools.filter((i) => toolSizes.lengthOf(i) != null);
  const asked = estimable.filter((i) => !drawn.includes(i));
  const at = asked[0] ?? -1;
  // The call is out. The answer is withheld while it runs: what is stored is the PREVIOUS
  // answer's reasoning, and a badge explaining the number being replaced is worse than none.
  const busy = run.progress !== null;
  // Which of the two estimates this part gets — measured off the photographs, or looked up in
  // the standard it was made to. The same test the run uses, so the badge cannot describe a
  // method the button does not follow.
  const picture = !fromStandard(getParts(docId)[at], categories[at] ?? '');

  // Read on every render rather than nudged by a counter: the run that writes it belongs
  // to the estimate store, not to this, so there is no moment here to bump one at.
  const thinking = at < 0 ? null : getEstimateThinking(docId, at);
  // One of those photographs, opened large. Its own state and its own lightbox: this panel is
  // the answer half, which is a block of text handed to the modal — where the question half
  // gets PromptPanel's zoom for free.
  const [zoom, setZoom] = useState<string | null>(null);
  const shots = thinking?.shots ?? [];
  // Which of them is open, so the zoom carries THAT picture's rectangle and its direction.
  // By URL, the set being one photograph each.
  const zoomed = shots.find((s) => listingImageSrc(s.url) === zoom);

  // Nothing on this tab has a size left to estimate.
  // DRAWN EVEN WITH NOTHING TO ASK ABOUT, greyed out: the phase shows its whole sequence.
  const nothingToAsk = at < 0 && !drawn.length;

  return (
    <div className="flex items-center gap-1.5">
      <CallBadge
        title={t('notice.parts.badgeEstimate')}
        named
        run={{
          label: run.progress && run.progress.total > 1
            ? `${t('notice.tip.menuRun')} · ${run.progress.done}/${run.progress.total}`
            : t('notice.tip.menuRun'),
          onRun: () => {
            for (const i of drawn) toolSizes.save(i);
            void runEstimates(docId, asked, productName, categories);
          },
          busy,
          disabled: disabled || nothingToAsk || (!productName && !drawn.length),
          onStop,
        }}
        // WHAT THIS PART'S ESTIMATE IS, and the two are not the same thing. A component is
        // measured here, in arithmetic over the boxes and the photographs' own scales, so
        // its first panel is that working rather than a prompt — there is no prompt, and
        // showing the old one would explain the answer by a call nobody made. Hardware is
        // still a question put to a model, and shows what it sends.
        //
        // WHICH IS WHY A COMPONENT'S BADGE IS ONE ENTRY AND NOT TWO. Nothing is sent, so
        // there is no question to keep beside the answer: before it has run the single panel
        // is what it WOULD do, and after it is what it did — the same arithmetic either way,
        // and offering both would be offering the same sum twice under two names. Hardware
        // keeps its pair, being a real call.
        noPrompt={picture || at < 0}
        // AND THE TOOLS' WORKING, where there are tools on this tab: each one's sums off the
        // drawings, or the line saying it had none and goes to the model instead.
        extra={tools.length ? [{
          key: 'tools',
          icon: <Ruler className="h-3.5 w-3.5" />,
          label: t('notice.parts.toolSizeRow'),
          panel: toolSizes.panel(tools),
        }] : undefined}
        promptLabel={t(picture ? 'notice.parts.estimateHow' : 'notice.parts.estimatePrompt')}
        // Nothing but tools the drawings size: no call, so nothing to preview.
        load={at < 0 ? () => Promise.resolve({ prompt: '' }) : picture
          ? async () => {
            const pixels = await measureImages(estimatePictures(docId, 99));
            const read = scaleEstimates(docId, at, pixels);
            return {
              prompt: scaleInputs(read),
              // THROUGH THE PROXY, which is the only address these load from. The panel was
              // handed the retailer's own URL — the string the crops are filed under — and
              // drew three broken images, which look exactly like a panel with no pictures.
              // See listingImageSrc.
              //
              // Captioned by the view, because the working above refers to them by exactly
              // that word: "front: box 240 x 88 px". A strip of three photographs of one
              // chair says nothing about which line is about which.
              urls: read.map((r) => ({
                url: listingImageSrc(r.url),
                caption: directionLabel(r.view),
                label: `${directionLabel(r.view)} · ${r.wide} × ${r.tall} px`,
              })),
            };
          }
          : () => estimatePrompt(docId, at, productName, categories)}
        // Withheld while the call is out, for the reason the second badge was hidden before:
        // what is stored is the PREVIOUS answer's reasoning, and an explanation of the number
        // being replaced is worse than none.
        answer={thinking && !busy ? {
          label: t('notice.parts.estimateThinking'),
          content: (
            <>
              {/* THE PHOTOGRAPHS IT MEASURED, above the account of them. They are also in the
                  first panel, and that is not a duplicate: this one says what each picture
                  GAVE, line by line, and reading "left: scale 1.241 mm/px y" without the
                  picture beside it is reading a claim about something you cannot see. */}
              {shots.length > 0 && (
                <div className="mb-3 flex flex-wrap items-start justify-center gap-2">
                  {shots.map((s) => (
                    <figure key={s.url} className="flex w-24 flex-col items-center gap-0.5">
                      {/* THE RECTANGLE THE NUMBERS CAME OUT OF, drawn on the picture with
                          those numbers on its edges. Without it the strip is two photographs
                          of a chair under a paragraph claiming one of its parts is 398 mm
                          across — a claim about a rectangle, shown as a claim about a chair.
                          BoxOverlay already writes a size along each edge; this is what that
                          is for. */}
                      <span
                        className="relative block cursor-zoom-in"
                        onClick={() => setZoom(listingImageSrc(s.url))}
                      >
                        <img
                          src={listingImageSrc(s.url)}
                          alt={directionLabel(s.view)}
                          className="h-24 w-24 rounded border border-slate-200 bg-white object-contain p-0.5 transition-opacity hover:opacity-80"
                        />
                        {/* Guarded, because a stored answer is whatever the run that wrote
                            it knew: the shots kept before the box was one of them have a URL
                            and a view and no rectangle, and destructuring one that is not
                            there throws where a picture would do. */}
                        {s.bbox && (
                          <BoxOverlay
                            box={s.bbox}
                            across={s.across ?? undefined}
                            up={s.up ?? undefined}
                            rounded="rounded"
                            // HATCHED AND GREY where the size did not come from this box —
                            // the mark this app already uses for a rectangle nobody believes.
                            // Only the biggest box is read; the others are shown because they
                            // are the working, and an unmarked one claims to be the answer.
                            // `used` is absent on answers stored before one was chosen, and
                            // those are left as they were rather than all struck through.
                            off={s.used === false}
                          />
                        )}
                      </span>
                      <figcaption className={`max-w-24 truncate text-[9px] font-semibold uppercase tracking-wide ${
                        s.used === false
                          ? 'text-slate-400 line-through'
                          : 'text-blue-700'
                      }`}
                      >
                        {directionLabel(s.view)}
                      </figcaption>
                    </figure>
                  ))}
                </div>
              )}
              {reasoningTooltip(thinking.reasoning, thinking.raw, t)}
            </>
          ),
        } : undefined}
      />
      {/* THE SET, so the arrows walk these two photographs. Without `srcs` the lightbox has
          nothing to step through and does not claim the arrow keys at all — which left them
          with the part selector behind the modal, and pressing → moved to another part while
          a picture of this one was open. See useStepThrough, which takes them in the capture
          phase precisely so an open overlay wins. */}
      {zoom && (
        <ImageLightbox
          src={zoom}
          alt=""
          srcs={shots.map((s) => listingImageSrc(s.url))}
          onNavigate={setZoom}
          view={zoomed && directionLabel(zoomed.view)}
          boxes={zoomed?.bbox ? [{
            box: zoomed.bbox,
            across: zoomed.across ?? undefined,
            up: zoomed.up ?? undefined,
            off: zoomed.used === false,
          }] : undefined}
          onClose={() => setZoom(null)}
        />
      )}
    </div>
  );
}
